<template>
  <div class="py-10 bg-gray-100 min-h-screen flex flex-col items-center">
    <div class="w-full prose font-medium px-2 text-justify tracking-tight" style="max-width: 75ch">
      <h2>
        <strong>PRIVACY NOTICE (PLATFORM, APP AND SERVICES)</strong>
        &nbsp;
      </h2>
      <h3>
        <strong>Introduction</strong>
        &nbsp;
      </h3>
      <p>
        Welcome to the Global Health Education Group&rsquo;s (&ldquo;GHEG&rdquo;) privacy policy.
      </p>
      <p>
        GHEG respects your privacy and is committed to protecting your personal data. This privacy
        policy will inform you as to how we look after your personal data when you use our app,
        platform or any services from us (regardless of where you visit it from) and tell you about
        your privacy rights and how the law protects you.
      </p>
      <p>
        This privacy policy is provided in a layered format so you can click through to the specific
        areas set out below. Please also use the Glossary to understand the meaning of some of the
        terms used in this privacy policy.&nbsp;
      </p>
      <p>1. IMPORTANT INFORMATION AND WHO WE ARE</p>
      <p>2. THE DATA WE COLLECT ABOUT YOU</p>
      <p>3.&nbsp;HOW IS YOUR PERSONAL DATA COLLECTED?</p>
      <p>4.&nbsp;HOW WE USE YOUR PERSONAL DATA</p>
      <p>5.&nbsp;DISCLOSURES OF YOUR PERSONAL DATA</p>
      <p>6. DATA SECURITY</p>
      <p>7. DATA RETENTION</p>
      <p>8. YOUR LEGAL RIGHTS</p>
      <p>9. GLOSSARY</p>
      <h3>
        <strong>1. Important information and who we are</strong>
        &nbsp;
      </h3>
      <p>
        <strong>Purpose of this privacy policy</strong>
        &nbsp;
      </p>
      <p>
        This privacy policy aims to give you information on how we collect and process your personal
        data through your use our app, platform or any services from us including any data you may
        provide us from time to time.
      </p>
      <p>We do not knowingly collect data relating to children.&nbsp;</p>
      <p>
        It is important that you read this privacy policy together with any other privacy policy or
        fair processing policy we may provide on specific occasions when we are collecting or
        processing personal data about you so that you are fully aware of how and why we are using
        your data. This privacy policy supplements other notices and privacy policies and is not
        intended to override them.
      </p>
      <p>
        <strong>Controller</strong>
        &nbsp;
      </p>
      <p>
        GHEG is the controller and responsible for your personal data ("we", "us" or "our" in this
        privacy policy).&nbsp;
      </p>
      <p>
        We have appointed a data privacy manager who is responsible for overseeing questions in
        relation to this privacy policy. If you have any questions about this privacy policy,
        including any requests to exercise your legal rights as set out in this document, please
        contact our data privacy manager using the details set out below.
      </p>
      <p>
        <strong>Contact details</strong>
        &nbsp;
      </p>
      <p>
        If you have any questions about this privacy policy or our privacy practices, please contact
        us at
        <a href="mailto:support@gheg.org"><em>support@gheg.org</em></a>
        .
      </p>
      <p>
        You have the right to make a complaint at any time to the Information Commissioner's Office
        (ICO), the UK regulator for data protection issues (www.ico.org.uk). We would, however,
        appreciate the chance to deal with your concerns before you approach the ICO so please
        contact us in the first instance.
      </p>
      <p>
        <strong>Changes to the privacy policy and your duty to inform us of changes</strong>
        &nbsp;
      </p>
      <p>
        We keep our privacy policy under regular review. This version was last updated on 26th
        January 2024. Historic versions can be obtained by contacting us.&nbsp;
      </p>
      <p>
        It is important that the personal data we hold about you is accurate and current. Please
        keep us informed if your personal data changes during your relationship with us.&nbsp;
      </p>
      <p>
        <strong>Third-party links</strong>
        &nbsp;
      </p>
      <p>
        The use of our app, platform and services may include links to third-party websites,
        plug-ins and applications. Clicking on those links or enabling those connections may allow
        third parties to collect or share data about you. We do not control these third-party
        websites and are not responsible for their privacy statements. We encourage you to read the
        privacy policy of every website or third party services that you visit.&nbsp;
      </p>
      <h3>
        <strong>2. The data we collect about you</strong>
        &nbsp;
      </h3>
      <p>
        Personal data, or personal information, means any information about an individual from which
        that person can be identified. It does not include data where the identity has been removed
        (anonymous data).&nbsp;
      </p>
      <p>
        We may collect, use, store and transfer different kinds of personal data about you which we
        have grouped together as follows:&nbsp;
      </p>
      <ul>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="15"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="1"
          data-aria-level="1"
        >
          <strong>Identity Data</strong>
          includes first name, maiden name, last name, username or similar identifier, title, date
          of birth and gender.&nbsp;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="15"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="2"
          data-aria-level="1"
        >
          <strong>Contact Data</strong>
          includes billing address, delivery address, email address and telephone numbers.&nbsp;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="15"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="3"
          data-aria-level="1"
        >
          <strong>Technical Data</strong>
          includes internet protocol (IP) address, your login data, browser type and version, time
          zone setting and location, browser plug-in types and versions, operating system and
          platform, and other technology on the devices you use to access our platform.
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="15"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="4"
          data-aria-level="1"
        >
          <strong>Profile Data</strong>
          includes your username and password, purchases or orders made by you, your interests,
          preferences, feedback and survey responses.
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="15"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="5"
          data-aria-level="1"
        >
          <strong>Usage Data</strong>
          includes information about how you use our products and services.
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="15"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="6"
          data-aria-level="1"
        >
          <strong>Marketing and Communications Data</strong>
          includes your preferences in receiving marketing from us and our third parties and your
          communication preferences.&nbsp;
        </li>
      </ul>
      <p>
        We also collect, use and share
        <strong>Aggregated Data</strong>
        such as statistical or demographic data for any purpose. Aggregated Data could be derived
        from your personal data but is not considered personal data in law as this data will
        <strong>not</strong>
        directly or indirectly reveal your identity. For example, we may aggregate your Usage Data
        to calculate the percentage of users accessing a specific website feature. However, if we
        combine or connect Aggregated Data with your personal data so that it can directly or
        indirectly identify you, we treat the combined data as personal data which will be used in
        accordance with this privacy policy.&nbsp;
      </p>
      <p>
        <strong>Special Categories of Personal Data</strong>
        &nbsp;
      </p>
      <p>
        The processing of special categories personnel data requires higher levels of protection. We
        need to have further justification for collecting, storing and using this type of personal
        information. We have in place an appropriate policy document and safeguards which we are
        required by law to maintain when processing such data. We will process special categories of
        personal information purely in order to provide the services to you and for no other
        purposes without your prior written consent.
      </p>
      <p>
        <strong>If you fail to provide personal data</strong>
        &nbsp;
      </p>
      <p>
        Where we need to collect personal data by law and you fail to provide that data when
        requested, we may not be able to perform the contract we have or are trying to enter into
        with you (for example, to provide you with services). In this case, we may have to cancel a
        product or service you have with us or access thereto but we will notify you if this is the
        case at the time.
      </p>
      <h3>
        <strong>3. How is your personal data collected?</strong>
        &nbsp;
      </h3>
      <p>We use different methods to collect data from and about you including through:&nbsp;</p>
      <ul>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="13"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1080,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="1"
          data-aria-level="1"
        >
          <strong>Direct interactions.</strong>
          You may give us your Identity and Contact by filling in forms or by corresponding with us
          email or otherwise. This includes personal data you provide when you:&nbsp;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="14"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="2"
          data-aria-level="1"
        >
          apply for our products or services;&nbsp;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="14"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="3"
          data-aria-level="1"
        >
          create an account with us;&nbsp;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="14"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="4"
          data-aria-level="1"
        >
          subscribe to our service or publications;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="14"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="5"
          data-aria-level="1"
        >
          request marketing to be sent to you; or&nbsp;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="14"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="6"
          data-aria-level="1"
        >
          give us feedback or contact us.
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="13"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1080,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="7"
          data-aria-level="1"
        >
          <strong>Automated technologies or interactions.</strong>
          We will automatically collect Technical Data about your equipment, browsing actions and
          patterns. We collect this personal data by using cookies, server logs and other similar
          technologies. We may also receive Technical Data about you if you visit other websites
          employing our cookies.
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="13"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1080,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="8"
          data-aria-level="1"
        >
          <strong>Third parties or publicly available sources.</strong>
          We may receive personal data about you from various third parties and public sources as
          set out below:
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="13"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1080,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="9"
          data-aria-level="1"
        >
          Technical Data from the following parties:&nbsp;
        </li>
      </ul>
      <ol>
        <li
          data-leveltext="(%3)"
          data-font=""
          data-listid="23"
          data-list-defn-props='{"335551500":0,"335552541":0,"335559685":1555,"335559991":561,"469769242":[65533,4],"469777803":"left","469777804":"(%3)","469777815":"multilevel"}'
          data-aria-posinset="1"
          data-aria-level="3"
        >
          analytics providers such as Google based outside the UK;
        </li>
      </ol>
      <ol>
        <li
          data-leveltext="(%3)"
          data-font=""
          data-listid="23"
          data-list-defn-props='{"335551500":0,"335552541":0,"335559685":1555,"335559991":561,"469769242":[65533,4],"469777803":"left","469777804":"(%3)","469777815":"multilevel"}'
          data-aria-posinset="2"
          data-aria-level="3"
        >
          search information providers.&nbsp;
        </li>
      </ol>
      <ul>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="13"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1080,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="10"
          data-aria-level="1"
        >
          Identity and Contact Data from data brokers or aggregators.&nbsp;
        </li>
      </ul>
      <h3>
        <strong>4. How we use your personal data</strong>
        &nbsp;
      </h3>
      <p>
        We will only use your personal data when the law allows us to. Most commonly, we will use
        your personal data in the following circumstances:&nbsp;
      </p>
      <ul>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="13"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1080,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="1"
          data-aria-level="1"
        >
          Where we need to provide access to services that you have requested access to.&nbsp;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="13"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1080,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="2"
          data-aria-level="1"
        >
          Where it is necessary for our legitimate interests (or those of a third party) and your
          interests and fundamental rights do not override those interests.&nbsp;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="13"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1080,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="3"
          data-aria-level="1"
        >
          Where we need to comply with a legal obligation.&nbsp;
        </li>
      </ul>
      <p>
        Below, we set out more information about the types of lawful basis that we will rely on to
        process your personal data.&nbsp;
      </p>
      <p>
        <strong>Purposes for which we will use your personal data</strong>
        &nbsp;
      </p>
      <p>
        We have set out below, in a table format, a description of all the ways we plan to use your
        personal data, and which of the legal bases we rely on to do so. We have also identified
        what our legitimate interests are where appropriate.&nbsp;
      </p>
      <p>
        Note that we may process your personal data for more than one lawful ground depending on the
        specific purpose for which we are using your data. Please contact us if you need details
        about the specific legal ground we are relying on to process your personal data where more
        than one ground has been set out in the table below.
      </p>
      <table data-table="" data-tablelook="1184">
        <tbody>
          <tr>
            <td data-celllook="4369">
              <p>
                <strong>Purpose/Activity</strong>
                &nbsp;
              </p>
            </td>
            <td data-celllook="4369">
              <p>
                <strong>Type of data</strong>
                &nbsp;
              </p>
            </td>
            <td data-celllook="4369">
              <p>
                <strong>Lawful basis for processing including basis of legitimate interest</strong>
                &nbsp;
              </p>
            </td>
          </tr>
          <tr>
            <td data-celllook="4369">
              <p>To register you as a new user&nbsp;</p>
            </td>
            <td data-celllook="4369">
              <p>(a) Identity</p>
              <p>(b) Contact&nbsp;</p>
            </td>
            <td data-celllook="4369">
              <p>Performance of contract/provision of services that you have signed up to&nbsp;</p>
            </td>
          </tr>
          <tr>
            <td data-celllook="4369">
              <p>To manage our relationship with you which will include:&nbsp;</p>
              <p>(a) Notifying you about changes to our terms or privacy policy&nbsp;</p>
              <p>(b) Asking you to leave a review or take a survey&nbsp;</p>
            </td>
            <td data-celllook="4369">
              <p>(a) Identity</p>
              <p>(b) Contact</p>
              <p>(c) Profile</p>
              <p>(d) Marketing and Communications&nbsp;</p>
            </td>
            <td data-celllook="4369">
              <p>(a) Performance of a contract with you</p>
              <p>(b) Necessary to comply with a legal obligation&nbsp;</p>
              <p>
                (c) Necessary for our legitimate interests (to keep our records updated and to study
                how customers use our products/services)&nbsp;
              </p>
            </td>
          </tr>
          <tr>
            <td data-celllook="4369">
              <p>
                To administer and protect our business and platform (including troubleshooting, data
                analysis, testing, system maintenance, support, reporting and hosting of data)
              </p>
            </td>
            <td data-celllook="4369">
              <p>(a) Identity&nbsp;</p>
              <p>(b) Contact&nbsp;</p>
              <p>(c) Technical&nbsp;</p>
            </td>
            <td data-celllook="4369">
              <p>
                (a) Necessary for our legitimate interests (for running our business, provision of
                administration and IT services, network security, to prevent fraud and in the
                context of a business reorganisation or group restructuring exercise)&nbsp;
              </p>
              <p>(b) Necessary to comply with a legal obligation&nbsp;</p>
            </td>
          </tr>
          <tr>
            <td data-celllook="4369">
              <p>
                To use data analytics to improve our website, products/services, marketing, customer
                relationships and experiences&nbsp;
              </p>
            </td>
            <td data-celllook="4369">
              <p>(a) Technical</p>
              <p>(b) Usage</p>
            </td>
            <td data-celllook="4369">
              <p>
                Necessary for our legitimate interests (to define types of customers for our
                products and services, to keep our website updated and relevant, to develop our
                business and to inform our marketing strategy)&nbsp;
              </p>
            </td>
          </tr>
          <tr>
            <td data-celllook="4369">
              <p>
                To make suggestions and recommendations to you about goods or services that may be
                of interest to you&nbsp;
              </p>
            </td>
            <td data-celllook="4369">
              <p>(a) Identity</p>
              <p>(b) Contact</p>
              <p>(c) Technical</p>
              <p>(d) Usage</p>
              <p>(e) Profile</p>
              <p>(f) Marketing and Communications&nbsp;</p>
            </td>
            <td data-celllook="4369">
              <p>
                Necessary for our legitimate interests (to develop our products/services and grow
                our business)&nbsp;
              </p>
            </td>
          </tr>
        </tbody>
      </table>
      <p>
        <strong>&nbsp;Promotional offers from us</strong>
        &nbsp;
      </p>
      <p>
        We may use your Identity, Contact, Technical, Usage and Profile Data to form a view on what
        we think you may want or need, or what may be of interest to you. This is how we decide
        which products, services and offers may be relevant for you (we call this marketing).
      </p>
      <p>
        You will receive marketing communications from us if you have requested information from us
        or purchased services from us and you have not opted out of receiving that marketing.&nbsp;
      </p>
      <p>
        <strong>Third-party marketing</strong>
        &nbsp;
      </p>
      <p>
        We will get your express opt-in consent before we share your personal data with any third
        party for marketing purposes.
      </p>
      <p>
        <strong>Opting out</strong>
        &nbsp;
      </p>
      <p>
        You can ask us or third parties to stop sending you marketing messages at any time by
        logging into the website and checking or unchecking relevant boxes to adjust your marketing
        preferences or by following the opt-out links on any marketing message sent to you or by
        contacting us at any time.
      </p>
      <p>
        <strong>Cookies</strong>
        &nbsp;
      </p>
      <p>
        You can set your browser to refuse all or some browser cookies, or to alert you when
        websites set or access cookies. If you disable or refuse cookies, please note that some
        parts of this website may become inaccessible or not function properly.
      </p>
      <p>
        <strong>Change of purpose</strong>
        &nbsp;
      </p>
      <p>
        We will only use your personal data for the purposes for which we collected it, unless we
        reasonably consider that we need to use it for another reason and that reason is compatible
        with the original purpose. If you wish to get an explanation as to how the processing for
        the new purpose is compatible with the original purpose, please contact us.
      </p>
      <p>
        If we need to use your personal data for an unrelated purpose, we will notify you and we
        will explain the legal basis which allows us to do so.&nbsp;
      </p>
      <p>
        Please note that we may process your personal data without your knowledge or consent, in
        compliance with the above rules, where this is required or permitted by law.&nbsp;
      </p>
      <h3>
        <strong>5. Disclosures of your personal data</strong>
        &nbsp;
      </h3>
      <p>
        We may share your personal data with the parties set out below for the purposes set out in
        the table above.&nbsp;
      </p>
      <ul>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="13"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1080,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="1"
          data-aria-level="1"
        >
          Third parties to whom we may choose to sell, transfer or merge parts of our business or
          our assets. Alternatively, we may seek to acquire other businesses or merge with them. If
          a change happens to our business, then the new owners may use your personal data in the
          same way as set out in this privacy policy.
        </li>
      </ul>
      <p>
        We require all third parties to respect the security of your personal data and to treat it
        in accordance with the law. We do not allow our third-party service providers to use your
        personal data for their own purposes and only permit them to process your personal data for
        specified purposes and in accordance with our instructions.&nbsp;
      </p>
      <h3>
        <strong>6. Data security</strong>
        &nbsp;
      </h3>
      <p>
        We have put in place appropriate security measures to prevent your personal data from being
        accidentally lost, used or accessed in an unauthorised way, altered or disclosed. In
        addition, we limit access to your personal data to those employees, agents, contractors and
        other third parties who have a business need to know. They will only process your personal
        data on our instructions and they are subject to a duty of confidentiality.
      </p>
      <p>
        We have put in place procedures to deal with any suspected personal data breach and will
        notify you and any applicable regulator of a breach where we are legally required to do
        so.&nbsp;
      </p>
      <h3>
        <strong>7. Data retention</strong>
        &nbsp;
      </h3>
      <p>
        <strong>How long will you use my personal data for?</strong>
        &nbsp;
      </p>
      <p>
        We will only retain your personal data for as long as reasonably necessary to fulfil the
        purposes we collected it for, including for the purposes of satisfying any legal,
        regulatory, tax, accounting or reporting requirements. We may retain your personal data for
        a longer period in the event of a complaint or if we reasonably believe there is a prospect
        of litigation in respect to our relationship with you.&nbsp;
      </p>
      <p>
        To determine the appropriate retention period for personal data, we consider the amount,
        nature and sensitivity of the personal data, the potential risk of harm from unauthorised
        use or disclosure of your personal data, the purposes for which we process your personal
        data and whether we can achieve those purposes through other means, and the applicable
        legal, regulatory, tax, accounting or other requirements.&nbsp;
      </p>
      <p>
        By law we have to keep basic information about our customers (including Contact, Identity,
        Financial and Transaction Data) for six years after they cease being customers for tax
        purposes.&nbsp;
      </p>
      <p>
        In some circumstances you can ask us to delete your data: see section 9 below for further
        information.&nbsp;
      </p>
      <p>
        In some circumstances we will anonymise your personal data (so that it can no longer be
        associated with you) for research or statistical purposes, in which case we may use this
        information indefinitely without further notice to you.
      </p>
      <h3>
        <strong>8. Your legal rights</strong>
        &nbsp;
      </h3>
      <p>
        Under certain circumstances, you have rights under data protection laws in relation to your
        personal data.
      </p>
      <p>
        If you wish to exercise any of the rights set out above, please contact us at
        support@gheg.org.&nbsp;
      </p>
      <p>
        <strong>No fee usually required</strong>
        &nbsp;
      </p>
      <p>
        You will not have to pay a fee to access your personal data (or to exercise any of the other
        rights). However, we may charge a reasonable fee if your request is clearly unfounded,
        repetitive or excessive. Alternatively, we could refuse to comply with your request in these
        circumstances.&nbsp;
      </p>
      <p>
        <strong>What we may need from you</strong>
        &nbsp;
      </p>
      <p>
        We may need to request specific information from you to help us confirm your identity and
        ensure your right to access your personal data (or to exercise any of your other rights).
        This is a security measure to ensure that personal data is not disclosed to any person who
        has no right to receive it. We may also contact you to ask you for further information in
        relation to your request to speed up our response.&nbsp;
      </p>
      <p>
        <strong>Time limit to respond</strong>
        &nbsp;
      </p>
      <p>
        We try to respond to all legitimate requests within one month. Occasionally it could take us
        longer than a month if your request is particularly complex or you have made a number of
        requests. In this case, we will notify you and keep you updated.&nbsp;
      </p>
      <h3>
        <strong>9. Glossary</strong>
        &nbsp;
      </h3>
      <p>
        <strong>LAWFUL BASIS</strong>
        &nbsp;
      </p>
      <p>
        <strong>Legitimate Interest</strong>
        means the interest of our business in conducting and managing our business to enable us to
        give you the best service/product and the best and most secure experience. We make sure we
        consider and balance any potential impact on you (both positive and negative) and your
        rights before we process your personal data for our legitimate interests. We do not use your
        personal data for activities where our interests are overridden by the impact on you (unless
        we have your consent or are otherwise required or permitted to by law). You can obtain
        further information about how we assess our legitimate interests against any potential
        impact on you in respect of specific activities by contacting us.&nbsp;
      </p>
      <p>
        <strong>Performance of Contract</strong>
        means processing your data where it is necessary for the performance of a contract to which
        you are a party or to take steps at your request before entering into such a contract.&nbsp;
      </p>
      <p>
        <strong>Comply with a legal obligation</strong>
        means processing your personal data where it is necessary for compliance with a legal
        obligation that we are subject to.&nbsp;
      </p>
      <p>
        <strong>THIRD PARTIES</strong>
        &nbsp;
      </p>
      <p>
        <strong>External Third Parties</strong>
        &nbsp;
      </p>
      <ul>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="13"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1080,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="1"
          data-aria-level="1"
        >
          HM Revenue &amp; Customs, regulators and other authorities acting as processors or joint
          controllers based in the United Kingdom who require reporting of processing activities in
          certain circumstances.&nbsp;
        </li>
      </ul>
      <p>
        <strong>YOUR LEGAL RIGHTS</strong>
        &nbsp;
      </p>
      <p>You have the right to:&nbsp;</p>
      <p>
        <strong>Request access</strong>
        to your personal data (commonly known as a "data subject access request"). This enables you
        to receive a copy of the personal data we hold about you and to check that we are lawfully
        processing it.&nbsp;
      </p>
      <p>
        <strong>Request correction</strong>
        of the personal data that we hold about you. This enables you to have any incomplete or
        inaccurate data we hold about you corrected, though we may need to verify the accuracy of
        the new data you provide to us.&nbsp;
      </p>
      <p>
        <strong>Request erasure</strong>
        of your personal data. This enables you to ask us to delete or remove personal data where
        there is no good reason for us continuing to process it. You also have the right to ask us
        to delete or remove your personal data where you have successfully exercised your right to
        object to processing (see below), where we may have processed your information unlawfully or
        where we are required to erase your personal data to comply with local law. Note, however,
        that we may not always be able to comply with your request of erasure for specific legal
        reasons which will be notified to you, if applicable, at the time of your request.
      </p>
      <p>
        <strong>Object to processing</strong>
        of your personal data where we are relying on a legitimate interest (or those of a third
        party) and there is something about your particular situation which makes you want to object
        to processing on this ground as you feel it impacts on your fundamental rights and freedoms.
        You also have the right to object where we are processing your personal data for direct
        marketing purposes. In some cases, we may demonstrate that we have compelling legitimate
        grounds to process your information which override your rights and freedoms.&nbsp;
      </p>
      <p>
        <strong>Request restriction of processing</strong>
        of your personal data. This enables you to ask us to suspend the processing of your personal
        data in the following scenarios:
      </p>
      <ul>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="15"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="2"
          data-aria-level="1"
        >
          If you want us to establish the data's accuracy.&nbsp;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="15"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="3"
          data-aria-level="1"
        >
          Where our use of the data is unlawful but you do not want us to erase it.&nbsp;
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="15"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="4"
          data-aria-level="1"
        >
          Where you need us to hold the data even if we no longer require it as you need it to
          establish, exercise or defend legal claims.
        </li>
        <li
          data-leveltext=""
          data-font="Symbol"
          data-listid="15"
          data-list-defn-props='{"335551500":0,"335552541":1,"335559685":1440,"335559991":360,"469769226":"Symbol","469769242":[8226],"469777803":"left","469777804":"","469777815":"hybridMultilevel"}'
          data-aria-posinset="5"
          data-aria-level="1"
        >
          You have objected to our use of your data but we need to verify whether we have overriding
          legitimate grounds to use it.
        </li>
      </ul>
      <p>
        <strong>Request the transfer</strong>
        of your personal data to you or to a third party. We will provide to you, or a third party
        you have chosen, your personal data in a structured, commonly used, machine-readable format.
        Note that this right only applies to automated information which you initially provided
        consent for us to use or where we used the information to perform a contract with you.
      </p>
      <p>
        <strong>Withdraw consent at any time</strong>
        where we are relying on consent to process your personal data. However, this will not affect
        the lawfulness of any processing carried out before you withdraw your consent. If you
        withdraw your consent, we may not be able to provide certain products or services to you. We
        will advise you if this is the case at the time you withdraw your consent.&nbsp;
      </p>
    </div>
  </div>
</template>
